import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useGetEmailsByPartyIdsQuery, useGetPartyMasterCustomQuery, useUpdatePartyMasterMutation } from '../api/sendEmail.api';
import { useSelector } from 'react-redux';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../components/snackBar/snakbar';
import { Recipient } from './useSendEmail';

export interface SendEmailModalProps {
  open: boolean;
  onClose: () => void;
  pageOptions: string[];
  initialPageType?: string;
  recipients?: Recipient[];
  onUpdateEmail: (id: number, newEmail: string) => void;
  setIsEditRecipient?: (id: number | null) => void;
  removeRecipient: (id: number) => void;
  isEditRecipient?: number | null;
}

export function useSendEmailModal(pageOptions: string[]) {
  const [open, setOpen] = useState(false);
  const [initialPageType, setInitialPageType] = useState<string>('');
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const recipientsRef = useRef<Recipient[]>([]);
  const [partyIDs, setPartyIDs] = useState<number[]>([]);
  const [isEditRecipient, setIsEditRecipient] = useState<number | null>(null);
  const onCloseCallbackRef = useRef<(() => void) | undefined>(undefined);

  // Keep ref in sync with state
  useEffect(() => {
    recipientsRef.current = recipients;
  }, [recipients]);

  const openModal = useCallback((pageType: string, newRecipients: Recipient[], onCloseCb?: () => void) => {
    setInitialPageType(pageType);
    const partyIds = newRecipients.map((recipient) => recipient.recipientId);
    setPartyIDs(partyIds);
    // Ensure POS invoices always have isActive: 1 and preserve existing recipient data
    const processedRecipients = newRecipients.map((recipient) => {
      if (recipient.invoiceType === 'PosInvoice') {
        return {
          ...recipient,
          isActive: 1, // POS invoices are always active
        };
      }
      // Preserve existing recipient data including isActive status
      return {
        ...recipient,
        isActive: recipient.isActive ?? 1,
      };
    });
    setRecipients(processedRecipients);
    onCloseCallbackRef.current = onCloseCb;
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
    if (onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
      onCloseCallbackRef.current = undefined;
    }
    setRecipients([]);
    setIsEditRecipient(null);
    setPartyIDs([]);
  }, []);

  const setIsEditRecipientMemo = useCallback((id: number | null) => {
    setIsEditRecipient(id);
  }, []);

  const isEditingPOSRecipient = useMemo(
    () => isEditRecipient ? recipients.some((r) => r.recipientId === isEditRecipient && r.invoiceType === 'PosInvoice') : false,
    [isEditRecipient, recipients]
  );

  const { data: partyData = [] } = useGetPartyMasterCustomQuery(
    { partyid: Number(isEditRecipient) },
    {
      refetchOnMountOrArgChange: false,
      skip: !isEditRecipient || isEditingPOSRecipient,
    }
  );

  const nonPOSPartyIDs = useMemo(
    () => partyIDs.filter((partyId) => {
      const recipient = recipients.find((r) => r.recipientId === partyId);
      return recipient && recipient.invoiceType !== 'PosInvoice';
    }),
    [partyIDs, recipients]
  );

  const { data: recipientDetails } = useGetEmailsByPartyIdsQuery({ partyIds: nonPOSPartyIDs }, { skip: !nonPOSPartyIDs.length });

  // Fix infinite loop: Only update recipients when recipientDetails changes, not when recipients changes
  useEffect(() => {
    if (!recipientDetails || recipientDetails.length === 0) {
      // Only set default values if recipients exist but no details
      if (recipients.length > 0) {
        setRecipients((prev) => {
          const hasChanges = prev.some((recipient) => {
            const isPOSInvoice = recipient.invoiceType === 'PosInvoice';
            // POS invoices should always have isActive: 1
            const expectedIsActive = isPOSInvoice ? 1 : (recipient.isActive ?? 1);
            const expectedEmail = recipient.email || '';
            return recipient.isActive !== expectedIsActive || recipient.email !== expectedEmail;
          });
          
          if (!hasChanges) return prev;
          
          return prev.map((recipient) => {
            const isPOSInvoice = recipient.invoiceType === 'PosInvoice';
            return {
              ...recipient,
              // POS invoices are always active, regardless of what was passed
              isActive: isPOSInvoice ? 1 : (recipient.isActive ?? 1),
              email: recipient.email || '',
            };
          });
        });
      }
      return;
    }

    setRecipients((prev) => {
      const updatedRecipients = prev.map((recipient) => {
        const isPOSInvoice = recipient.invoiceType === 'PosInvoice';
        
        // POS invoices should never be matched with recipientDetails (they're filtered out)
        // So they should always go to the else branch and get isActive: 1
        if (isPOSInvoice) {
          // Ensure POS invoices always have isActive: 1
          if (recipient.isActive !== 1) {
            return {
              ...recipient,
              isActive: 1,
            };
          }
          return recipient;
        }
        
        // For non-POS invoices, try to find matching recipient details
        const matchingRecipient = recipientDetails.find((details) => details?.partyid === recipient.recipientId);
        
        if (matchingRecipient) {
          const updated = {
            ...recipient,
            name: !recipient.name ? matchingRecipient?.partydisplayname || '' : recipient.name,
            email: matchingRecipient.email,
            isActive: matchingRecipient.is_active,
          };
          // Only return new object if something actually changed
          if (
            recipient.name === updated.name &&
            recipient.email === updated.email &&
            recipient.isActive === updated.isActive
          ) {
            return recipient;
          }
          return updated;
        }
        
        // No matching recipient found for non-POS invoice
        const defaultRecipient = {
          ...recipient,
          isActive: recipient.isActive ?? 1,
          email: recipient.email || '',
        };
        
        // Only return new object if something changed
        if (
          recipient.isActive === defaultRecipient.isActive &&
          recipient.email === defaultRecipient.email
        ) {
          return recipient;
        }
        return defaultRecipient;
      });
      
      // Only update if there are actual changes
      const hasChanges = updatedRecipients.some((updated, index) => updated !== prev[index]);
      return hasChanges ? updatedRecipients : prev;
    });
  }, [recipientDetails]); // Remove recipients from dependencies to prevent infinite loop

  const currentFY = useSelector((state: any) => state.userPrefrenceandReducer?.financialYear);
  const [updatePartyMaster] = useUpdatePartyMasterMutation();

  const updateEmail = useCallback(
    async (id: number, newEmail: string) => {
      try {
        // Find recipient from ref to avoid closure issues
        const recipient = recipientsRef.current.find((r) => r.recipientId === id);
        const isPOSInvoice = recipient?.invoiceType === 'PosInvoice';

        // Update local state optimistically
        setRecipients((prev) => prev.map((r) => (r.recipientId === id ? { ...r, email: newEmail } : r)));

        if (isPOSInvoice) {
          // For POS invoices, just update local state (already done above)
          setIsEditRecipient(null);
          return;
        }

        // For regular invoices, update party master
        if (!partyData?.[0]) {
          showErrorSnackbar('Party data not found', '', '');
          setIsEditRecipient(null);
          return;
        }

        const input = {
          ...partyData[0],
          email: newEmail,
          companybranchid: 0,
          expiredtimestamp: 0,
          financialyearid: currentFY?.financialyearid,
          lastupdatedby: currentFY?.userid,
        };
        if (input?.enteredtimestamp) {
          delete input?.enteredtimestamp;
        }
        
        await updatePartyMaster({ createPartyMasterInput: input }).unwrap();
        showSuccessSnackbar('Email updated successfully', 'right', 'bottom');
      } catch (error) {
        showErrorSnackbar('Failed to update email', 'right', 'bottom');
      } finally {
        setIsEditRecipient(null);
      }
    },
    [partyData, currentFY, updatePartyMaster]
  );

  const removeRecipient = useCallback((id: number) => {
    setRecipients((prev) => {
      const filtered = prev.filter((recipient) => recipient.id !== id);
      if (filtered.length === 0) {
        setOpen(false);
      }
      return filtered;
    });
  }, []);

  const modalProps = useMemo<SendEmailModalProps>(
    () => ({
      open,
      onClose: closeModal,
      pageOptions,
      initialPageType,
      recipients,
      onUpdateEmail: updateEmail,
      isEditRecipient,
      setIsEditRecipient: setIsEditRecipientMemo,
      removeRecipient,
    }),
    [open, closeModal, pageOptions, initialPageType, recipients, updateEmail, isEditRecipient, setIsEditRecipientMemo, removeRecipient]
  );

  return { openModal, closeModal, modalProps };
}
